import { handleRequest } from "..";
import { httpAuthorized } from "@/main";

// ==================== 终端设备管理 ====================

export interface DeviceItem {
  endpointId: string;
  deviceId: string;
  name: string;
  activated: boolean;
  activatedAt: string | null;
  enabled: number;
  channel: number;
  certBatchId: string;
  batchCode: string;
  createtime: string;
  batchCreateTime: string | null;
  /** 绑定船舶 ID（null=未绑船，前端"迁移设备"按钮显隐用） */
  shipId: string | null;
  /** 绑定船舶名称（null=未绑船） */
  shipName: string | null;
  /** 在线状态（ONLINE/OFFLINE，前端"迁移设备"按钮置灰用） */
  status: "ONLINE" | "OFFLINE";
}

/**
 * 查询全量设备列表（含批次信息）
 */
export const getAllDevicesApi = async (): Promise<DeviceItem[] | null> => {
  return handleRequest<DeviceItem[]>(
    httpAuthorized.get("/certBatch/devices/all"),
    (data) => data.data.devices
  );
};

/**
 * 录入终端设备（每次自动创建新证书批次）
 */
export const preRegisterDevicesApi = async (
  devices: { deviceId: string; name?: string }[],
  remark?: string,
  certBatchId?: string
): Promise<any[] | null> => {
  return handleRequest<any[]>(
    httpAuthorized.post("/certBatch/devices/preRegister", {
      certBatchId,
      devices,
      remark,
    }),
    (data) => data.data.results
  );
};

/**
 * 删除已禁用设备
 */
export const deleteDeviceApi = async (
  endpointId: string
): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.delete(`/certBatch/devices/${endpointId}`),
    (data) => data.data.success
  );
};

// ==================== 终端操作 ====================

/**
 * 切换单个终端的启用/禁用状态
 */
export const toggleEndpointStatusApi = async (
  endpointId: string
): Promise<{ enabled: number; action: string } | null> => {
  return handleRequest<{ enabled: number; action: string }>(
    httpAuthorized.post("/certBatch/endpoint/toggle", { endpointId }),
    (data) => data.data
  );
};

/**
 * 更换设备硬件
 */
export const replaceHardwareApi = async (
  endpointId: string,
  newDeviceId: string,
  newName?: string,
  remark?: string
): Promise<any | null> => {
  return handleRequest<any>(
    httpAuthorized.post("/certBatch/endpoint/replaceHardware", {
      endpointId,
      newDeviceId,
      newName,
      remark,
    }),
    (data) => data.data.endpoint
  );
};

// ==================== 迁移设备协议（换硬件配套） ====================

/** 单台设备迁移结果 */
export interface MigrateResultItem {
  /** 设备英文键名（协议文件名去 .json 后缀） */
  enName: string;
  /** 设备中文名 */
  devName: string;
  /** 协议文件名（null=库内无协议文件记录，不可迁移） */
  protName: string | null;
  /** 是否迁移成功（null=未参与本轮下发，如清单预览/未勾选） */
  success: boolean | null;
  /** 失败说明（成功时为 null） */
  errorMsg: string | null;
}

/** 迁移设备协议结果 */
export interface MigrateResult {
  endpointId: string;
  shipId: string;
  shipName: string;
  total: number;
  results: MigrateResultItem[];
  /** 整体失败原因（下发层面失败时全部为失败），正常逐台对账时为 null */
  reason: string | null;
}

/**
 * 迁移设备协议（换硬件后向新终端重新下发设备协议文件 [5,5]）
 *
 * <p>不走 handleRequest（会吞掉服务端错误信息）：迁移的失败原因
 * （终端离线 / 未绑船 / 无设备 / 超时）对用户有操作指导意义，需完整透传。</p>
 *
 * @param endpointId 终端 ID（必须已绑定船舶且在线）
 * @param devNames   待迁移设备英文键名列表；空数组/不传 = 迁移该船全部设备
 */
export const migrateDeviceProtocolsApi = async (
  endpointId: string,
  devNames?: string[]
): Promise<MigrateResult> => {
  try {
    const response = await httpAuthorized.post(
      "/certBatch/endpoint/migrateDeviceProtocols",
      {
        endpointId,
        devNames: devNames && devNames.length > 0 ? devNames : undefined,
      }
    );
    return response.data.data.result as MigrateResult;
  } catch (e: any) {
    const data = e?.response?.data;
    const raw =
      typeof data === "string"
        ? data.replace(/^An unexpected error occurred:\s*/, "")
        : data?.message || data?.info || "";
    throw new Error(raw || "迁移失败：服务无响应");
  }
};

// ==================== 证书下载 ====================

/**
 * 切换设备频道
 */
export const changeChannelApi = async (
  endpointId: string,
  channel: number
): Promise<any | null> => {
  return handleRequest<any>(
    httpAuthorized.post("/certBatch/endpoint/changeChannel", {
      endpointId,
      channel,
    }),
    (data) => data.data.endpoint
  );
};

/**
 * 下载证书批次完整包（ZIP）
 */
export const downloadCertBatchPackageApi = async (
  id: string,
  fileName: string
): Promise<void> => {
  const response = await httpAuthorized.get(`/certBatch/download/${id}`, {
    responseType: "blob",
  });
  let finalName = fileName;
  if (!finalName) {
    const disposition = response.headers["content-disposition"];
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match) finalName = match[1].replace(/['"]/g, "");
    }
  }
  if (!finalName) finalName = "certs.zip";

  const blob = new Blob([response.data], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = finalName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};
